import React, { useState } from 'react';
import AIWidget from './AIWidget';
import TiltedCard from './TiltedCard';
import { useEventCards } from '../hooks/useEventCards';

const PlatformOverview = ({ data, formatNumber, errors, hasAPIKeys }) => {
  const [configStatus, setConfigStatus] = useState({});
  
  // Use the event cards hook for tilt effects
  useEventCards();
  
  // Fetch configuration status for each platform
  React.useEffect(() => {
    const fetchConfigStatus = async () => {
      try {
        const response = await fetch('https://cgb-backend.carl-g-bisaillon.workers.dev/api/config/status');
        if (response.ok) {
          const data = await response.json();
          setConfigStatus(data.config || {});
        }
      } catch (error) {
        console.error('Failed to fetch configuration status:', error);
      }
    };
    fetchConfigStatus();
  }, []);
  
  // Only show platforms that are configured
  const configuredPlatforms = [
    {
      key: 'facebook',
      name: 'Facebook Pages',
      icon: 'fab fa-facebook-f',
      className: 'facebook',
      isConfigured: configStatus.facebook?.configured || false,
      stats: configStatus.facebook?.configured ? [
        { label: 'Abonnés', value: formatNumber(data?.facebook?.followers || 0) },
        { label: 'Portée', value: formatNumber(data?.facebook?.reach || 0) },
        { label: 'Engagement', value: (data?.facebook?.engagement || 0) + '%' }
      ] : [],
      trend: configStatus.facebook?.configured ? (data?.facebook?.trend || '+0%') : '',
      isReel: true
    },
    {
      key: 'instagram',
      name: 'Instagram',
      icon: 'fab fa-instagram',
      className: 'instagram',
      isConfigured: configStatus.instagram?.configured || false,
      stats: configStatus.instagram?.configured ? [
        { label: 'Abonnés', value: formatNumber(data?.instagram?.followers || 0) },
        { label: 'Portée', value: formatNumber(data?.instagram?.reach || 0) },
        { label: 'Engagement', value: (data?.instagram?.engagement || 0) + '%' }
      ] : [],
      trend: configStatus.instagram?.configured ? (data?.instagram?.trend || '+0%') : '',
      isReel: true
    },
    {
      key: 'tiktok',
      name: 'TikTok',
      icon: 'fab fa-tiktok',
      className: 'tiktok',
      isConfigured: configStatus.tiktok?.configured || false,
      stats: configStatus.tiktok?.configured ? [
        { label: 'Abonnés', value: formatNumber(data?.tiktok?.followers || 0) },
        { label: 'Vues', value: formatNumber(data?.tiktok?.views || 0) },
        { label: 'J\'aime', value: formatNumber(data?.tiktok?.likes || 0) }
      ] : [],
      trend: configStatus.tiktok?.configured ? (data?.tiktok?.trend || '+0%') : '',
      isReel: true
    },
    {
      key: 'youtube',
      name: 'YouTube Shorts',
      icon: 'fab fa-youtube',
      className: 'youtube',
      isConfigured: configStatus.youtube?.configured || false,
      stats: configStatus.youtube?.configured ? [
        { label: 'Abonnés', value: formatNumber(data?.youtube?.subscribers || 0) },
        { label: 'Vues', value: formatNumber(data?.youtube?.views || 0) },
        { label: 'Shorts', value: formatNumber(data?.youtube?.shorts || 0) }
      ] : [],
      trend: configStatus.youtube?.configured ? (data?.youtube?.trend || '+0%') : '',
      isReel: true
    },
    {
      key: 'google',
      name: 'Google Analytics',
      icon: 'fab fa-google',
      className: 'google',
      isConfigured: configStatus.google?.configured || false,
      stats: configStatus.google?.configured ? [
        { label: 'Sessions', value: formatNumber(data?.google?.sessions || 0) },
        { label: 'Pages vues', value: formatNumber(data?.google?.pageViews || 0) },
        { label: 'Taux de rebond', value: (data?.google?.bounceRate || 0) + '%' }
      ] : [],
      trend: configStatus.google?.configured ? (data?.google?.trend || '+0%') : '',
      isReel: true
    },
    {
      key: 'cloudflare',
      name: 'Cloudflare',
      icon: 'fab fa-cloudflare',
      className: 'cloudflare',
      isConfigured: configStatus.cloudflare?.configured || false,
      stats: configStatus.cloudflare?.configured ? [
        { label: 'Requêtes', value: formatNumber(data?.cloudflare?.requests || 0) },
        { label: 'Bande passante', value: formatNumber(data?.cloudflare?.bandwidth || 0) },
        { label: 'Cache hit', value: (data?.cloudflare?.cacheHit || 0) + '%' }
      ] : [],
      trend: configStatus.cloudflare?.configured ? (data?.cloudflare?.trend || '+0%') : '',
      isReel: true
    }
  ].filter(platform => platform.isConfigured); // Only show configured platforms

  // If no platforms are configured, show a message
  if (configuredPlatforms.length === 0) {
    return (
      <section className="platform-overview">
        <div className="container">
          <h2>APERÇU DES PLATEFORMES</h2>
          <div className="no-platforms-message">
            <div className="no-platforms-icon">🔧</div>
            <h3>Aucune API configurée</h3>
            <p>Configurez au moins une API pour voir les analyses de vos plateformes.</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="platform-overview">
      <div className="container">
        <h2>APERÇU DES PLATEFORMES</h2>
        <div className="platforms-grid">
          {configuredPlatforms.map((platform) => (
            <TiltedCard
              key={platform.key}
              imageSrc={`https://via.placeholder.com/400x300/2c3e50/FFE1AF?text=${platform.name.replace(' ', '+')}`}
              altText={`${platform.name} Analytics`}
              captionText={`${platform.name} - ${platform.stats.length} métriques`}
              containerHeight="400px"
              containerWidth="100%"
              imageHeight="300px"
              imageWidth="100%"
              rotateAmplitude={12}
              scaleOnHover={1.05}
              showMobileWarning={false}
              showTooltip={true}
              displayOverlayContent={true}
              overlayContent={
                <div className="platform-overlay-content">
                  <div className="platform-overlay-header">
                    <div className="platform-overlay-icon">
                      <i className={platform.icon}></i>
                    </div>
                    <h3>{platform.name}</h3>
                  </div>
                  
                  <div className="platform-overlay-description">
                    {platform.key === 'facebook' && "Suivez vos performances sur Facebook Pages avec des analyses détaillées et des insights en temps réel."}
                    {platform.key === 'instagram' && "Analysez vos performances Instagram avec des métriques d'engagement et de portée."}
                    {platform.key === 'tiktok' && "Suivez vos vidéos TikTok et analysez les performances de votre contenu."}
                    {platform.key === 'youtube' && "Analysez vos YouTube Shorts et le rendement de votre chaîne."}
                    {platform.key === 'google' && "Accédez aux données Google Analytics pour comprendre le comportement de vos visiteurs."}
                    {platform.key === 'cloudflare' && "Surveillez les performances de votre site web avec les données Cloudflare."}
                  </div>

                  <div className="platform-overlay-metrics">
                    <h4>MÉTRIQUES:</h4>
                    <div className="platform-overlay-metrics-grid">
                      {platform.stats.map((stat, index) => (
                        <div key={index} className="platform-overlay-metric-item">
                          <span className="platform-overlay-metric-value">{stat.value}</span>
                          <span className="platform-overlay-metric-label">{stat.label}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="platform-overlay-status">
                    <div className="platform-overlay-status-indicator">
                      <i className="fas fa-chart-line"></i>
                      <span>Actif</span>
                    </div>
                  </div>

                  <div className="platform-overlay-actions">
                    <button className="platform-overlay-action-btn primary">
                      Voir Détails
                    </button>
                    <button className="platform-overlay-action-btn secondary">
                      Analyser
                    </button>
                  </div>
                </div>
              }
            />
          ))}
        </div>
        
        <AIWidget />
      </div>
    </section>
  );
};

export default PlatformOverview;